import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import noop from "lodash/noop";
import classesPropType from "@xo-union/classes-prop-type";
import { useClasses } from "@xo-union/react-css-modules";
import { useUniqueID } from "@xo-union/react-unique-id";
import { Caption } from "@xo-union/tk-ui-typography";
import upperFirst from "lodash/upperFirst";
import lowerCase from "lodash/lowerCase";
import styles from "./styles.css";
import WithValidations from "../WithValidations.js";
import SubText from "../SubText.js";
const Textarea = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    classes: classesProp,
    defaultValue,
    id: idProp,
    maxLength,
    name,
    placeholder,
    state,
    subText,
    value,
    label = upperFirst(lowerCase(name)),
    onChange = noop,
    ...restOfProps
  } = props;
  const classes = useClasses(styles, classesProp);
  const id = useUniqueID(idProp);
  const isValueControlledRef = React.useRef(value !== undefined);
  let inputLength;
  let handleChange;
  if (isValueControlledRef.current) {
    inputLength = value.length;
    handleChange = onChange;
  } else {
    /* eslint-disable react-hooks/rules-of-hooks */
    const [inputLengthState, setInputLength] = React.useState((defaultValue || '').length);
    handleChange = evt => {
      setInputLength(evt.target.value.length);
      onChange(evt);
    };
    inputLength = inputLengthState;
  }
  const atInputLimit = inputLength >= maxLength;
  const classMap = {
    neutral: classes['is-neutral'],
    invalid: classes['is-invalid'],
    valid: classes['is-valid']
  };
  const validationClass = classMap[state];
  const lengthIndicatorValidationClass = atInputLimit ? classes['length-indicator-invalid'] : classes['length-indicator-valid'];
  const dataHasValue = inputLength > 0 ? '' : undefined;
  let placeholderIfNoLabel;
  let textAreaClasses = `${classes.input} ${validationClass}`;
  if (!label) {
    placeholderIfNoLabel = placeholder;
    textAreaClasses += ` ${classes.placeholder}`;
  }
  let subtextId;
  let lengthDescriptionId;
  let labelId;
  let ariaDescribedby = '';
  if (label) {
    labelId = `${id}-label`;
  }
  if (subText) {
    subtextId = `${id}-subtext`;
    ariaDescribedby += `${id}-subtext`;
  }
  if (maxLength) {
    lengthDescriptionId = `${id}-length`;
    ariaDescribedby += ` ${lengthDescriptionId}`;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classes.container
  }, /*#__PURE__*/React.createElement("textarea", _extends({
    ref: ref,
    className: textAreaClasses,
    "data-has-value": dataHasValue,
    defaultValue: defaultValue,
    id: id,
    name: name,
    placeholder: placeholderIfNoLabel,
    value: value,
    "aria-describedby": ariaDescribedby,
    "aria-labelledby": labelId,
    onChange: handleChange
  }, restOfProps)), label && /*#__PURE__*/React.createElement("label", {
    className: classes.label,
    htmlFor: id,
    id: labelId
  }, label), subText && /*#__PURE__*/React.createElement(SubText, {
    state: state,
    className: classes['sub-text'],
    id: subtextId
  }, subText), maxLength && /*#__PURE__*/React.createElement(Caption, {
    className: `${classes['length-indicator']} ${lengthIndicatorValidationClass}`,
    id: lengthDescriptionId
  }, `${inputLength}/${maxLength}`));
});
Textarea.defaultProps = {
  defaultValue: undefined,
  label: undefined,
  maxLength: undefined,
  onChange: noop,
  placeholder: undefined,
  subText: '',
  state: 'neutral',
  value: undefined
};
const validationProp = process.env.NODE_ENV !== "production" ? PropTypes.any : {};
process.env.NODE_ENV !== "production" ? Textarea.propTypes = {
  /**
   * Used to generate the field's name, id and label
   * */
  name: PropTypes.string.isRequired,
  /**
   * Text to display as label or `false` to not display a label.
   * */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * This prop represents the text inside the textarea. When this prop is passed in this component becomes a controlled component. The value shown in the text field will be whatever is passed down as the value prop and the only way to change the value in the text field would be to update the value prop.
   * */
  value: PropTypes.string,
  /**
   * Prepopulates the field with a default value. Use this prop only if you want `<Textarea />` to be an uncontrolled component. If you want to prepopulate the field and have this be a controlled component then you should pass in the value prop instead.
   * */
  defaultValue: PropTypes.string,
  /**
   * A function that will run after every keypress inside the text field.
   */
  onChange: PropTypes.func,
  /**
   * Placeholder text for input field that is only rendered if label is `false`.
   * */
  placeholder: PropTypes.string,
  /**
   * Visual state. This has no effect when validations are enabled.
   */
  state: PropTypes.oneOf(['neutral', 'valid', 'invalid']),
  /**
   * Subtext message used to provide the user with extra information. The subText will appear just below the field.
   *
   *
   * When validations are enabled, this value is used for the `neutral` and the `valid` state (if `subTextOnValid` is not specified). The given validators will determine what subText will render on `invalid`, unless otherwise specified by the `subTextOnInvalid` prop.
   */
  subText: PropTypes.node,
  /**
   * Max length is the max number of characters that can go into a textarea
   */
  maxLength: PropTypes.number,
  /**
   * Override the default id (which is derived from the name)
   * */
  id: PropTypes.string,
  /**
   * Classes dependencies
   */
  classes: classesPropType(['container', 'input', 'is-invalid', 'is-valid', 'label', 'length-indicator-exceeded', 'length-indicator', 'placeholder', 'sub-text']),
  /**
   * Validations to apply. See [Input Component](/packages/@xo-union/ui-form-validations#input).
   */
  validations: validationProp,
  /**
   * This is only used when validations are enabled.
   *
   * What subText to render when state is `invalid`. When a function is provided, the function will receive the errors generated by the validators. The return value should be a React.Node type.
   */
  subTextOnInvalid: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * This is only used when validations are enabled.
   *
   * What subText to render when state is `valid`. This is only used when validations are enabled.
   */
  subTextOnValid: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
} : void 0;
export default WithValidations(Textarea);